import { useMemo, useState } from 'react'

import { ConfigProvider, Menu, Spin } from 'antd'
import { useTheme } from 'antd-style'
import { last } from 'lodash'

import { HelpCatalogueEntity } from '@eastcoal/api/content/interface'

import { DataEmpty } from '@eastcoal/common/components/DataEmpty'
import { DataError } from '@eastcoal/common/components/DataError'
import { pick } from '@eastcoal/common/utils/object'

import { useNavigate } from '@eastcoal/router/exports/react-router'

import { useHomeStore } from '../../Store/hooks/useStore'

type MenuItem = {
  key: string
  label: string
  children?: MenuItem[]
}

export const DocMenu = () => {
  const { data, error, loading, refreshData, currentCatalogues } = useHomeStore(
    state =>
      pick(state, [
        'data',
        'error',
        'loading',
        'refreshData',
        'currentDocId',
        'currentCatalogues'
      ])
  )

  const items = useMemo(() => {
    const transform = (list: HelpCatalogueEntity[] = []): MenuItem[] => {
      return list?.map(item => ({
        key: `${item.id}`,
        label: `${item.name}`,
        children: transform(item.children)
      }))
    }
    return transform(data)
  }, [data])

  const selectedKeys = useMemo(() => {
    return currentCatalogues.map(item => `${item.id}`)
  }, [currentCatalogues])

  const [manualOpenKeys, setManualOpenKeys] = useState<string[]>([])
  const mergedOpenKeys = useMemo(
    () => Array.from(new Set([...manualOpenKeys, ...selectedKeys])),
    [manualOpenKeys, selectedKeys]
  )
  const defaultOpenKeys = useMemo(() => {
    return items
      .flatMap(item => item.children)
      .filter(Boolean as unknown as (item?: MenuItem) => item is MenuItem)
      .map(item => item.key)
  }, [items])

  const navigate = useNavigate()
  const theme = useTheme()

  return (
    <Spin spinning={loading}>
      {error ? (
        <DataError error={error} reload={refreshData} />
      ) : items.length ? (
        <ConfigProvider
          theme={{
            token: {
              fontSize: theme.fontSizeLG
            }
          }}
        >
          <Menu
            defaultOpenKeys={defaultOpenKeys}
            items={items}
            mode='inline'
            openKeys={mergedOpenKeys}
            selectedKeys={selectedKeys}
            onOpenChange={setManualOpenKeys}
            onSelect={({ selectedKeys }) => {
              navigate({
                search: `?id=${last(selectedKeys)}`
              })
            }}
          />
        </ConfigProvider>
      ) : (
        <DataEmpty reload={refreshData} />
      )}
    </Spin>
  )
}
